import type { AdminOverview } from '@steado/admin-contract/overview';

import { Panel, StatStrip } from '@/components/panel/panels';
import { formatTokens, formatUsd } from '@/lib/format-spend';

import { CostChart } from './CostChart';

/**
 * What the models cost, over the window.
 *
 * The headline sits in the card's header rather than in the strip below,
 * because it is the number the screen exists to show and the three under the
 * chart are the ones that explain it. `costIsPartial` is stated beside it in
 * warn ink for the same reason: a total that is really a floor must never read
 * as a total.
 */
export function SpendPanel({ overview }: { readonly overview: AdminOverview }) {
  const { spend } = overview;

  return (
    <Panel
      title="Model spend"
      action={
        <span className="flex flex-col items-end gap-px">
          {/* The hero figure, the same one `StatCards` draws. */}
          <span className="font-mono text-2xl leading-none font-medium tracking-[-0.025em] tabular-nums text-foreground">
            {formatUsd(spend.costUsd)}
          </span>
          {spend.costIsPartial ? (
            <span className="text-xs text-warn-fg">a floor, not a total</span>
          ) : (
            <span className="text-xs text-muted-foreground">
              over {overview.windowDays} day{overview.windowDays === 1 ? '' : 's'}
            </span>
          )}
        </span>
      }
      bleed
      footer={
        <StatStrip
          items={[
            { value: formatTokens(spend.inputTokens), label: 'Input tokens' },
            { value: formatTokens(spend.outputTokens), label: 'Output tokens' },
            {
              // The denominator arrived with `spend.calls`. Without it this
              // screen could only say what the month cost, never whether the
              // bill moved because usage grew or because a model did.
              value:
                spend.costUsd === null || spend.calls === 0
                  ? '—'
                  : formatUsd(spend.costUsd / spend.calls, false),
              label: 'Mean cost per call',
            },
          ]}
        />
      }
    >
      <div className="border-t border-border px-[18px] pt-3.5 pb-1">
        <CostChart series={spend.series} windowDays={overview.windowDays} />
      </div>
    </Panel>
  );
}
